import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  ConstructionOutlined,
  PublicOutlined,
} from "@mui/icons-material";
import { GetItemCommand, UpdateItemCommand } from "@aws-sdk/client-dynamodb";
import { tokens } from "../theme/theme";
import { CHROME } from "../public/halloween/season";

/**
 * Admin control for the public site's maintenance mode.
 *
 * Flips `maintenance` on the single `site_config` row that the public site
 * reads (see MAINTENANCE.md). While it is on, the home page and the catalog are
 * replaced by a landing page; shared listing links, the crossword and the
 * contacts page keep working.
 *
 * Two pieces, deliberately split: a banner that only ever *reports* the state,
 * and one button in the top bar that changes it. A switch alongside a banner
 * gave two controls for one setting; this way the status is always visible and
 * there is exactly one thing to press.
 *
 * The write goes through the ADMIN client — the same authenticated credentials
 * the rest of the panel uses. The public site's anonymous role can only read
 * this row, and must stay that way: anything a guest can write, anyone on the
 * internet can write.
 *
 * IAM: the admin role needs `dynamodb:GetItem` and `dynamodb:UpdateItem` on
 * `site_config`. UpdateItem creates the row if it is missing, so no PutItem.
 */

const TABLE = "site_config";
const ROW_ID = "site";

export function useMaintenanceFlag(ddbClient, enabled) {
  const [value, setValue] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(
    function () {
      if (!ddbClient || !enabled) return;
      var alive = true;

      ddbClient
        .send(
          new GetItemCommand({
            TableName: TABLE,
            Key: { id: { S: ROW_ID } },
          })
        )
        .then(function (data) {
          if (!alive) return;
          var on = !!(data.Item && data.Item.maintenance && data.Item.maintenance.BOOL);
          setValue(on);
          setReady(true);
        })
        .catch(function (err) {
          if (!alive) return;
          // Unlike the public site, the panel says so out loud: an admin who
          // cannot read the flag must not be shown a control that lies.
          setError(describe(err, "прочитати"));
          setReady(true);
        });

      return function () {
        alive = false;
      };
    },
    [ddbClient, enabled]
  );

  const change = useCallback(
    function (next) {
      if (!ddbClient) return;
      setBusy(true);
      setError(null);

      ddbClient
        .send(
          new UpdateItemCommand({
            TableName: TABLE,
            Key: { id: { S: ROW_ID } },
            UpdateExpression: "SET maintenance = :m",
            ExpressionAttributeValues: { ":m": { BOOL: !!next } },
          })
        )
        .then(function () {
          setValue(!!next);
          setBusy(false);
        })
        .catch(function (err) {
          setError(describe(err, "змінити"));
          setBusy(false);
        });
    },
    [ddbClient]
  );

  return { value: value, ready: ready, busy: busy, error: error, change: change };
}

/** Turns an AWS error into something a realtor can act on. */
function describe(err, verb) {
  var name = err && err.name ? err.name : "";
  if (name === "AccessDeniedException") {
    return (
      "Немає прав, щоб " +
      verb +
      " режим обслуговування. Ролі адміністратора потрібні " +
      "dynamodb:GetItem і dynamodb:UpdateItem на таблицю site_config " +
      "(див. MAINTENANCE.md)."
    );
  }
  if (name === "ResourceNotFoundException") {
    return "Таблиці site_config не існує — створіть її (див. MAINTENANCE.md).";
  }
  return "Не вдалося " + verb + " режим обслуговування: " + (err && err.message ? err.message : name);
}

/* -------------------------------------------------------------------------- */
/* The one control                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Labelled by what it will DO, not by the current state — a button that says
 * "Обслуговування" leaves you guessing whether that is the status or the action.
 */
export function MaintenanceButton({ state }) {
  if (state.error || !state.ready) return null;

  const on = state.value;

  return (
    <Button
      size="small"
      variant={on ? "contained" : "outlined"}
      disableElevation
      disabled={state.busy}
      onClick={function () {
        state.change(!on);
      }}
      startIcon={
        state.busy ? (
          <CircularProgress size={15} color="inherit" />
        ) : on ? (
          <PublicOutlined sx={{ fontSize: 17 }} />
        ) : (
          <ConstructionOutlined sx={{ fontSize: 17 }} />
        )
      }
      sx={{
        whiteSpace: "nowrap",
        // Icon-only on a phone, where the top bar is already crowded.
        minWidth: 0,
        px: { xs: 1, sm: 2 },
        "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
        ...(on
          ? {
              backgroundColor: tokens.sand600,
              color: tokens.surface,
              "&:hover": { backgroundColor: tokens.sand700 },
            }
          : CHROME
          ? {
              // Light on the night app bar, where ink500 all but disappears.
              borderColor: "rgba(255,255,255,0.28)",
              color: "rgba(255,255,255,0.78)",
              "&:hover": {
                borderColor: tokens.sand500,
                color: tokens.sand500,
                backgroundColor: "rgba(232,169,61,0.1)",
              },
            }
          : {
              borderColor: tokens.line,
              color: tokens.ink500,
              "&:hover": {
                borderColor: tokens.sand600,
                color: tokens.sand700,
                backgroundColor: tokens.sand50,
              },
            }),
      }}
    >
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        {on ? "Увімкнути сайт" : "Вимкнути сайт"}
      </Box>
    </Button>
  );
}

/**
 * Always on screen while an admin is in the panel: blue when the public site is
 * live, orange when it is not. The orange one matters most — without it, the
 * only sign the site is switched off is a button label, which is easy to set on
 * a Friday and forget. The blue one is the reassurance that it is not.
 */
export function MaintenanceBanner({ state }) {
  if (!state.ready) return null;

  if (state.error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 0, alignItems: "center", py: 0.5 }}>
        <Typography variant="body2">{state.error}</Typography>
      </Alert>
    );
  }

  const on = state.value;

  return (
    <Alert
      icon={on ? <ConstructionOutlined /> : <PublicOutlined />}
      sx={{
        borderRadius: 0,
        alignItems: "center",
        py: on ? 0.75 : 0.75,
        backgroundColor: on ? tokens.sand100 : tokens.blue50,
        color: on ? tokens.sand700 : tokens.blue700,
        "& .MuiAlert-icon": { color: "inherit", py: 0, alignItems: "center" },
        "& .MuiAlert-message": { py: 0 },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {on
          ? "Публічний сайт не активний. Відвідувачі не бачать головної сторінки й каталогу."
          : "Публічний сайт активний"}
      </Typography>
      {/* {on && (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.9 }}>
          Відвідувачі не бачать головної сторінки й каталогу. Посилання на
          окремі квартири, кросворд і контакти працюють. Зміна доходить до
          відвідувачів протягом хвилини.
        </Typography>
      )} */}
    </Alert>
  );
}
